import { Stack, Tooltip } from '@mui/material';
import { ResourceType, type InventoryResource } from '@repo/api-gw-sdk';
import { useRouter } from 'next/navigation';

import { ThreatIssueIcon } from '@/components/security/threatIcons';
import { ClickableText } from '@/components/shared/clickableText';
import { CellActionsWrapper } from '@/components/table/cellActionsWrapper';
import type { AppRow, AppTable } from '@/components/table/tanstackTable';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';

import { hasThreatFindings } from '../../inventoryAlerts/alertsModel';
import { hasSecurityIssue } from '../../security/securityUtils';

interface ResourceNameCellProps {
  row: AppRow<InventoryResource>;
  table: AppTable<InventoryResource>;
  showThreatIndicator?: boolean;
}

export const ResourceNameCell = ({
  row,
  table,
  showThreatIndicator,
}: ResourceNameCellProps) => {
  const router = useRouter();

  const handleClick = () => {
    if (row.original.resourceType === ResourceType.GoogleWorkspaceResource) {
      router.push(`/explorer/${encodeURIComponent(row.original.id)}`);
      return;
    }

    if (row.getIsSelected()) {
      table.resetRowSelection(true);
    } else {
      table.setRowSelection({
        [row.id]: true,
      });
    }
  };

  return (
    <BasicResourceNameCell
      row={row}
      onClick={handleClick}
      showThreatIndicator={showThreatIndicator}
    />
  );
};

// A table that shows threats in a column of their own turns the threat styling off here.
export const BasicResourceNameCell = ({
  row,
  onClick,
  showThreatIndicator = true,
}: {
  row: AppRow<InventoryResource>;
  onClick?: () => void;
  showThreatIndicator?: boolean;
}) => {
  const { InventoryStatusColumns } = useFeatureFlags();
  if (!row.original.resourceName) {
    return null;
  }
  // With the status columns on, findings left over from before a resource was excluded don't mark it as a threat.
  const hasThreat = InventoryStatusColumns
    ? hasThreatFindings(row.original)
    : hasSecurityIssue(row.original);

  if (!showThreatIndicator) {
    return (
      <CellActionsWrapper textToCopy={row.original.resourceName}>
        <ClickableText
          data-testid='resource-name-cell'
          onClick={onClick}
          sx={{ position: 'relative' }}
        >
          {row.original.resourceName}
        </ClickableText>
      </CellActionsWrapper>
    );
  }

  return (
    <CellActionsWrapper textToCopy={row.original.resourceName}>
      {hasThreat ? (
        <Stack
          direction='row'
          sx={{
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Tooltip title='Threat findings in the latest snapshot.'>
            <Stack>
              <ThreatIssueIcon />
            </Stack>
          </Tooltip>
          <ClickableText
            data-testid='resource-name-cell'
            baseColor='var(--mui-palette-error-main)'
            hoverColor='var(--mui-palette-error-light)'
            onClick={onClick}
          >
            {row.original.resourceName}
          </ClickableText>
        </Stack>
      ) : (
        <ClickableText
          data-testid='resource-name-cell'
          onClick={onClick}
          sx={{
            lineHeight: '24px',
            position: 'relative',
          }}
        >
          {row.original.resourceName}
        </ClickableText>
      )}
    </CellActionsWrapper>
  );
};
